import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, CheckCircle2 } from 'lucide-react';
import posthog from 'posthog-js';

interface InviteOnlyViewProps {
  /** PostHog tracking context */
  source: 'signup_page' | 'login_google_error';
  /** Show "Already have an account? Sign in" link (used on signup page) */
  showSignInLink?: boolean;
  /** Callback to return to the previous view (used on login page) */
  onBack?: () => void;
}

const InviteOnlyView = ({ source, showSignInLink = false, onBack }: InviteOnlyViewProps) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputStyles = {
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: 'white',
    padding: '10px 16px',
    fontSize: '16px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    height: '40px',
    letterSpacing: '-0.16px',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitted) return;

    setIsSubmitting(true);

    try {
      posthog.capture('invite_only_access_requested', {
        email,
        source,
        timestamp: new Date().toISOString(),
      });
    } catch {
      // PostHog may be blocked by ad blockers — still show success
    }

    // Brief delay for tactile feedback
    await new Promise((r) => setTimeout(r, 300));
    setIsSubmitting(false);
    setSubmitted(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col items-center gap-6"
    >
      {/* Lock icon */}
      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
        <Lock className="h-5 w-5 text-[#a4a4a4]" />
      </div>

      {/* Copy */}
      <p className="text-sm text-[#a4a4a4] text-center tracking-[-0.14px] leading-relaxed">
        Hobbes is currently invite-only. Request access below or reach out to your organization admin for an invitation link.
      </p>

      {/* Email capture form / Success state */}
      <AnimatePresence mode="wait">
        {!submitted ? (
          <motion.form
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 w-full"
          >
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyles}
              className="focus:border-white/30 placeholder:text-[#b2b2b2]"
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base tracking-[-0.16px]"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Request Access'
              )}
            </button>
          </motion.form>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 p-3 rounded-xl w-full"
          >
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Thanks! We'll be in touch soon.</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Divider */}
      <div className="flex items-center gap-4 w-full">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-[#a4a4a4] tracking-[-0.12px]">Or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Meet the Hobbes Team CTA */}
      <a
        href="https://cal.com/team/hobbes/hobbes-intro"
        target="_blank"
        rel="noopener noreferrer"
        className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors flex items-center justify-center gap-2 text-base tracking-[-0.16px]"
      >
        Meet the Hobbes Team
      </a>

      {/* Sign in link (signup page) */}
      {showSignInLink && (
        <div className="text-center text-sm text-[#a4a4a4] tracking-[-0.14px]">
          Already have an account?{' '}
          <Link to="/login" className="text-white underline underline-offset-4 hover:text-white/80 transition-colors">
            Sign in
          </Link>
        </div>
      )}

      {/* Back link (login page) */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[#a4a4a4] tracking-[-0.14px] hover:text-white transition-colors"
        >
          Back to sign in
        </button>
      )}
    </motion.div>
  );
};

export default InviteOnlyView;
